import * as React from 'react';
import {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DateTimePicker from '@mui/lab/DateTimePicker';
import config from '../../../app/config';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Paper from '@mui/material/Paper';
import NavigateBeforeSharpIcon from '@mui/icons-material/NavigateBeforeSharp';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {ReportsAPI} from './reports_api';
import API from '../../../app/api';
import {setSnackbar} from '../../../store/reducers/globals';
import {setLoading} from '../../../store/reducers/patient';
import {useDispatch} from 'react-redux';
import Chip from '@mui/material/Chip';
import {DatabaseIconHL7, DateIconHL7} from '../../../components/icons/icons';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import _ from 'lodash';
import {RowMessage, saveDataToCSV, useTableStyles} from './table_utils';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';

const columnsWidths = {
    'Info': '100px',
    'Date': '300px',
    'Created': '300px',
    'Type': '170px',
    'Hospital number': '170px',
    'Attendance': '200px',
    'System': '200px',
    'Version': '200px',
    'Actions': '200px',
    'Patient': '200px',
    'Status': '170px',
    'Last Send Time': '200px',
    'Last Status': '170px',
    'Hl7 Type': '170px',
};


const UserLoginAuditTable = forwardRef((props, ref) => {

    const isEyonix = process.env.BUILD_MODULE === "main";
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const controller = new AbortController();
    const classes = useTableStyles();

    useImperativeHandle(ref, () => ({
        exportCsv: () => {
            dispatch(setLoading(true));
            let csv = props.columns.map((column) => {
                return column.name;
            }).join(',');
            getSetFilters();

            ReportsAPI.userLastLogin(getFilter(0, 100000)).then(tableData => {
                if (tableData?.data?.data) {
                    csv = saveDataToCSV(csv,tableData.data.data,props.columns);
                }
            }).finally(() => {
                const a = document.createElement("a");
                a.href = URL.createObjectURL(
                    new Blob([csv], { type: 'text/csv' })
                );
                a.setAttribute("download", "usersLastLogin.csv");
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                dispatch(setLoading(false));
            });
        },
    }));

    const [messages, setMessages] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [allPages, setAllPages] = React.useState(1);
    const [sortSend, setSortSend] = React.useState(null);
    const [sortOrder, setSortOrder] = React.useState(false);
    const [initStage, setInitStage] = React.useState(true);

    const [selectedRows, setSelectedRows] = React.useState([]);

    useEffect(() => {
        if (props.columns.length) {
            let defaultSortItem = _.find(props.columns, {defaultSort: true});
            if (defaultSortItem) {
                setSortSend(defaultSortItem.value);
            } else {
                setSortSend('!publishDate');
            }
        }

    }, [props.columns]);

    const [searchCriteria, setSearchCriteria] = React.useState({
        lastLoginBefore: 183,
        userName: null
    });

    const getFilter = () => {
        let filter = {
            days_since_last_login: searchCriteria.lastLoginBefore
        };
        return filter;
    };

    const getDocReportData = () => {
        if (sortSend) {
            dispatch(setLoading(true));
            getSetFilters();
            ReportsAPI.userLastLogin(getFilter(), controller.signal).then(tableData => {
                if (tableData?.data?.data) {
                    setMessages(tableData.data.data);
                }
            }).finally(() => {
                dispatch(setLoading(false));
            }).catch(error => {
                dispatch(setSnackbar({
                    type: 'error',
                    message: t('Unknown error while fetching report data'),
                }));
            });
        }
    };

    useEffect(() => {
        return () => {
            controller.abort();
        };
    }, []);

    const [filtersChip, setFilterChips] = React.useState([]);

    const applyFilter = () => {
        setInitStage(false);
        if (!page) {
            getDocReportData();
        } else
            setPage(0);
    };

    const getSetFilters = () => {
        let filters = [];

        if (searchCriteria.lastLoginBeforeDays) filters.push({filed: 'lastLoginBeforeDays', value: searchCriteria.lastLoginBeforeDays});
        //if (searchCriteria.userName) filters.push({filed: 'userName', value: searchCriteria.userName});

        setFilterChips(filters);
    };

    useEffect(() => {
        if (doSearch.current) {
            applyFilter();
            doSearch.current = false;
        }
    }, [searchCriteria]);

    const doSearch = useRef(false);

    const handleDelete = (chip) => {
        doSearch.current = true;
        setSearchCriteria(prevState => ({...prevState, [chip.filed]: ''}));
    };

    const renderFilters = () => {
        return (<Grid
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{padding: '40px', borderRadius: '5px', marginBottom: '10px', border: '1px solid #448FFF'}}
        >
            <Grid item xs={12} style={{display: 'flex', alignItems: 'center'}}>
                <Typography variant="subtitle2" sx={{mr: 2}}>
                    {t("Users not accessing system for more then")}
                </Typography>
                <AgyleTextField
                    type="number"
                    min={0}
                    max={99999}
                    textAlign="center"
                    value={searchCriteria.lastLoginBefore}
                    onChange={(event) => {
                        setSearchCriteria(prevState => ({...prevState, lastLoginBefore: event.target.value}));
                    }}
                    variant="outlined"
                    style={{width: 60, padding: "0px"}}
                    size="small"
                    borderRadius={4}
                />
                <Typography variant="subtitle2" sx={{mx: 1.5}}>
                    {t("days")}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Button variant="contained" className={classes.agyleButton} onClick={applyFilter}>Apply</Button>
            </Grid>
            <Grid item xs={12}>
                {filtersChip.map((filter) => {
                    return <Chip label={filter.value} onDelete={() => handleDelete(filter)}/>;
                })}
            </Grid>
        </Grid>);
    };

    const addRow = (row) => {
        setSelectedRows((prRows) => ([...prRows, row]));
    };

    const removeRow = (row) => {
        let selectedRowsTmp = selectedRows.filter(item => item !== row);
        setSelectedRows(selectedRowsTmp);
    };

    useEffect(() => {
        if (sortSend && !initStage)
            getDocReportData();
    }, [sortSend, sortOrder, page]);


    const handleChangeNextPage = () => {
        setPage(page + 1);
    };

    const handleChangePrevPage = () => {
        setPage(page - 1);
    };

    const resendMessages = () => {
        let promises = [];

        selectedRows.forEach((row) => {
            promises.push(API.sendMessageHL7(row.message, controller.signal));
        });

        Promise.all(promises).then((values) => {
            values.forEach((val) => {
                if (val.status === 'ok')
                    dispatch(setSnackbar({type: 'info', message: ` The message has been resent!`}));
                else
                    dispatch(setSnackbar({type: 'error', message: ` The message was not sent!`}));
            });
        });

    };

    const getColumnWidth = column => (columnsWidths[column?.description] || '256px');


    const handleSetSort = (column) => {
        if (column.value === sortSend) {
            setSortOrder(!sortOrder);
        } else {
            setSortSend(column.value);
        }
    };

    const getSortIcon = (columnName) => {
        if (columnName !== sortSend) return '';
        if (sortOrder) return <ArrowDropDownIcon/>;
        return <ArrowDropUpIcon/>;

    };
    return (<div>
        {!props.filterHidden && renderFilters()}
        {!props.outcoming && !props.filterHidden && selectedRows.length > 0 && <React.Fragment>
            <Button className={classes.agyleButton}
                    onClick={() => {}}
                    size="small"
                    color="primary"
                    variant="contained">
                {t('copy')}
            </Button>
            <Button className={classes.agyleButton}
                    onClick={resendMessages}
                    size="small"
                    color="primary"
                    variant="contained">
                {t('resend')}
            </Button>
        </React.Fragment>}

        <Paper sx={{width: '100%', overflow: 'hidden', mt: 4}}>
            <TableContainer>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead key={'HEAD'}>
                        <TableRow>
                            {props.columns.map((column, indexColumn) => {
                                return (
                                    <TableCell
                                        key={indexColumn + 'Hasd'}
                                        align={column.align}
                                        style={{
                                            minWidth: column.minWidth,
                                            width: column.maxWidth || getColumnWidth(column),
                                            border: '1px solid #80808035',
                                        }}
                                    >
                                        <Grid container justifyContent="space-between">
                                            <Typography variant="subtitle2"
                                                        color="textSecondary"
                                                        style={{cursor: column.sort ? 'pointer' : ''}}
                                                        onClick={() => {
                                                            if (column.sort) {
                                                                handleSetSort(column);
                                                            }
                                                        }}>
                                                <div style={{display: 'flex'}}>
                                                    {column.description} {getSortIcon(column.value)}
                                                </div>

                                            </Typography>
                                            {indexColumn === props.columns.length - 1 &&
                                            <DatabaseIconHL7 onClick={props.openColumnManager} style={{cursor: "pointer"}}/>}
                                            {column.name === 'dateTime' && <DateIconHL7/>}
                                        </Grid>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {messages?.map((row, rowIndex) => {
                            // console.log("test", row);
                            return <React.Fragment key={rowIndex + 'div'}>
                                <RowMessage columns={props.columns}
                                            messagePath={props.messagePath}
                                            row={row} handleAddRow={addRow}
                                            rowIndex={rowIndex}
                                            outcoming={props.outcoming || false}
                                            handleRemoveRow={removeRow}/>
                            </React.Fragment>;
                        })}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={8}>
                                <IconButton size="small"
                                            disabled={page < 1}
                                            onClick={handleChangePrevPage}>
                                    <NavigateBeforeSharpIcon fontSize="small"/>
                                </IconButton>
                                Page: {page + 1} from {allPages}
                                <IconButton size="small"
                                            disabled={(page+1) >= allPages}
                                            onClick={handleChangeNextPage}>
                                    <NavigateNextIcon fontSize="small"/>
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Paper>

    </div>);
})

export default UserLoginAuditTable;